import React, { useMemo } from "react";
import { useAuth } from "../../../context/AuthContext";
import CertificatePreview from "../../../components/common/CertificatePreview";
import { premiumError, premiumRequest } from "../../../services/Premium";
import { useToast } from "../../../components/common/toasts/ToastContext";
import HighlightedPage from "./sources/HighlightedPage";

/** Ten sam podgląd co w kroku 5 zwykłego wniosku, z API zleceń Premium. */
export default function OrderFilePreview({
  path,
  fileName = "Podgląd świadectwa.pdf",
  downloadLabel = "Pobierz podgląd PDF",
  revision,
  onReadyChange,
  source,
}) {
  const { accessToken } = useAuth();
  const { notify } = useToast();
  const firstPage = source?.page || 1;
  const rotation = source?.rotation;
  const draft = revision != null;
  const documentSource = useMemo(() => {
    const pages = new Map();
    const pagePath = draft ? path : `${path}/preview`;
    const request = draft ? { method: "POST", data: { revision } } : {};
    return {
      initialPage: firstPage - 1,
      previewLabel: draft ? "świadectwa" : "dokumentu",
      loadPage: async ({ page, useCache, signal }) => {
        if (!useCache) pages.clear();
        if (pages.has(page)) return pages.get(page);
        const orientation =
          page + 1 === firstPage && rotation != null
            ? `?rotation=${rotation}`
            : "";
        const result = await premiumRequest(
          accessToken,
          `${pagePath}/pages/${page + 1}${orientation}`,
          { ...request, signal },
        );
        const image = {
          blob: new Blob(
            [Uint8Array.from(atob(result.image), (char) => char.charCodeAt(0))],
            {
              type: "image/png",
            },
          ),
          pageCount: result.pageCount,
        };
        if (!signal.aborted) pages.set(page, image);
        return image;
      },
      download: async () => {
        const blob = await premiumRequest(accessToken, path, {
          ...request,
          responseType: "blob",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        // Przeglądarka musi zdążyć rozpocząć pobieranie przed zwolnieniem adresu.
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      },
      downloadLabel,
      downloadProgressLabel: draft ? "Generowanie PDF" : "Pobieranie pliku",
    };
  }, [
    accessToken,
    path,
    revision,
    draft,
    fileName,
    downloadLabel,
    firstPage,
    rotation,
  ]);
  return (
    <section
      className={
        source
          ? "premium-certificate-preview"
          : "card premium-panel premium-certificate-preview"
      }
      aria-label={draft ? "Podgląd świadectwa PDF" : fileName}
    >
      {!draft && <h2>{fileName}</h2>}
      <CertificatePreview
        documentSource={documentSource}
        renderPage={({ page, url }) =>
          page === firstPage && source?.regions?.length > 0 ? (
            <HighlightedPage
              key={url}
              url={url}
              regions={source.regions}
              title={`${fileName} · strona ${page}`}
              onError={() =>
                notify("Nie udało się wyświetlić zaznaczenia.", "danger")
              }
            />
          ) : null
        }
        onSuccess={() => onReadyChange?.(true)}
        onFailure={(error) => {
          onReadyChange?.(false);
          if (!draft) notify(premiumError(error), "danger");
        }}
        setIsDocumentRefreshing={(refreshing) => {
          if (refreshing) onReadyChange?.(false);
        }}
      />
    </section>
  );
}
